import { useMemo } from 'react';

import { Box, Typography } from '@material-ui/core';
import { alpha, makeStyles } from '@material-ui/core/styles';
import BrokenImageOutlinedIcon from '@material-ui/icons/BrokenImageOutlined';
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined';
import MicNoneOutlinedIcon from '@material-ui/icons/MicNoneOutlined';
import PermMediaOutlinedIcon from '@material-ui/icons/PermMediaOutlined';
import VideocamOutlinedIcon from '@material-ui/icons/VideocamOutlined';

import LegendaMensagem from 'components/LegendaMensagem';
import dayjs from 'services/dayjs';
import { TiposMensagemEnum } from 'store/reducers/mensagens';
import { ContatoMencionado } from 'types';

type MensagemMidiaRemovidaProps = {
  tipoMidia: TiposMensagemEnum;
  removidoPorAdminEm: string | Date;
  legenda?: string;
  isMe?: boolean;
  contatosMencionados?: ContatoMencionado[];
};

const useStyles = makeStyles(({ palette, spacing }) => ({
  root: {
    minWidth: spacing(28),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1.5),
    padding: spacing(1.5, 2),
    backgroundColor: 'transparent',
  },
  iconContainer: {
    display: 'flex',
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    width: spacing(5),
    height: spacing(5),
    borderRadius: '50%',
    color: palette.error.main,
    backgroundColor: alpha(palette.error.main, 0.1),
  },
  icon: {
    fontSize: spacing(2.5),
  },
  content: {
    minWidth: 0,
  },
  caption: {
    paddingInline: spacing(2),
    paddingBottom: spacing(1),
    borderTop: `1px dashed ${palette.divider}`,
    backgroundColor: alpha(palette.error.main, 0.05),
    '& .MuiTypography-root': {
      color: `${alpha(palette.text.primary, 0.72)} !important`,
    },
    '& .MuiSvgIcon-root': {
      color: alpha(palette.text.primary, 0.72),
    },
  },
  removalInfo: {
    opacity: 0.75,
  },
}));

export default function MensagemMidiaRemovida(
  props: MensagemMidiaRemovidaProps,
) {
  const { tipoMidia, removidoPorAdminEm, legenda, isMe, contatosMencionados } =
    props;
  const classes = useStyles();

  const fallback = useMemo(() => {
    switch (tipoMidia) {
      case TiposMensagemEnum.AUDIO:
        return <MicNoneOutlinedIcon className={classes.icon} />;
      case TiposMensagemEnum.DOCUMENTO:
        return <InsertDriveFileOutlinedIcon className={classes.icon} />;
      case TiposMensagemEnum.IMAGEM:
        return <BrokenImageOutlinedIcon className={classes.icon} />;
      case TiposMensagemEnum.VIDEO:
        return <VideocamOutlinedIcon className={classes.icon} />;
      default:
        return <PermMediaOutlinedIcon className={classes.icon} />;
    }
  }, [classes.icon, tipoMidia]);

  const secondary = useMemo(() => {
    const date = dayjs(removidoPorAdminEm);
    return `Mídia removida pelo administrador em ${date.format(
      'DD/MM/YYYY',
    )} às ${date.format('HH:mm')}`;
  }, [removidoPorAdminEm]);

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.iconContainer}>{fallback}</Box>
        <Box className={classes.content}>
          <Typography
            className={classes.removalInfo}
            color="inherit"
            variant="caption"
          >
            {secondary}
          </Typography>
        </Box>
      </Box>

      {legenda && (
        <Box className={classes.caption}>
          <LegendaMensagem
            legenda={legenda}
            isMe={isMe}
            contatosMencionados={contatosMencionados}
          />
        </Box>
      )}
    </Box>
  );
}
